/** Widget window IPC bridge. */

import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@heidi/runtime/logger';
import { invoke } from '@tauri-apps/api/core';
import { emit, listen, type EventCallback } from '@tauri-apps/api/event';

export const WIDGET_OPEN_FEEDBACK_HISTORY_EVENT =
  'widget:open-dictation-feedback-history';

/** Get the last successful transcript text (for copy button). */
export async function widgetGetLastTranscript(): Promise<string | null> {
  if (!isTauri()) return null;
  return invoke<string | null>('widget_get_last_transcript');
}

/**
 * Copy the last transcript to the system clipboard via Rust.
 *
 * The widget's non-activating NSPanel doesn't grant the WebView "transient
 * user activation", so `navigator.clipboard.writeText()` is rejected.
 * This bypasses the browser restriction by writing to the native pasteboard.
 *
 * @returns `true` if text was copied, `false` if no transcript available.
 */
export async function widgetCopyTranscript(): Promise<boolean> {
  if (!isTauri()) return false;
  return invoke<boolean>('widget_copy_transcript');
}

/** Signal that the widget WebView has completed its first render cycle. */
export async function widgetContentReady(): Promise<void> {
  if (!isTauri()) return;
  return invoke<void>('widget_content_ready');
}

/** Show the dictation widget window. */
export async function widgetShow(): Promise<void> {
  if (!isTauri()) return;
  return invoke<void>('widget_show');
}

/** Hide the dictation widget window. */
export async function widgetHide(): Promise<void> {
  if (!isTauri()) return;
  return invoke<void>('widget_hide');
}

/** Ask the main Heidi window to open Dictate history, then bring it forward. */
export async function widgetOpenFeedbackHistory(): Promise<void> {
  if (!isTauri()) return;
  try {
    await emit(WIDGET_OPEN_FEEDBACK_HISTORY_EVENT);
  } catch (error) {
    logger.warn('[dictation/feedback-history] failed to emit open-history event', {
      errorName: error instanceof Error ? error.name : typeof error,
      ...sanitizeError(error),
    });
    throw error;
  }

  try {
    await invoke<void>('widget_focus_main_window');
  } catch (error) {
    logger.warn('[dictation/feedback-history] focus main window failed', {
      errorName: error instanceof Error ? error.name : typeof error,
      ...sanitizeError(error),
    });
  }
}

/** Main-window listener for the feedback confirmation's history action. */
export async function listenWidgetOpenFeedbackHistory(cb: EventCallback<void>) {
  if (!isTauri()) return () => {};
  return listen<void>(WIDGET_OPEN_FEEDBACK_HISTORY_EVENT, cb);
}

/** Sync the SCR-7131 cursor-display positioning flag to Rust. Off = primary monitor. */
export async function setWidgetCursorDisplayPositioning(enabled: boolean): Promise<void> {
  if (!isTauri()) return;
  return invoke<void>('widget_set_cursor_display_positioning', { enabled });
}

/** Bubble bounds in widget-page CSS pixels (top-left origin). */
export interface WidgetHitRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Report the bubble's current rect; `null` = full pass-through. */
export async function widgetSetHitRect(rect: WidgetHitRect | null): Promise<void> {
  if (!isTauri()) return;
  return invoke<void>('widget_set_hit_rect', { rect });
}

/** Route keyboard events to the non-activating widget while its text field is edited. */
export async function widgetSetKeyboardFocus(focused: boolean): Promise<void> {
  if (!isTauri()) return;
  return invoke<void>('widget_set_keyboard_focus', { focused });
}

/**
 * Native hover enter on the widget (macOS tracking area, Windows cursor poll).
 * Backs up the WebView's own hover events, which can be dropped during key or
 * click-through transitions.
 */
export async function listenWidgetMouseEntered(cb: EventCallback<void>) {
  if (!isTauri()) return () => {};
  return listen<void>('widget:mouse-entered', cb);
}

/** Native hover exit counterpart to `listenWidgetMouseEntered`. */
export async function listenWidgetMouseExited(cb: EventCallback<void>) {
  if (!isTauri()) return () => {};
  return listen<void>('widget:mouse-exited', cb);
}

/**
 * Escape key pressed while the widget is visible. Emitted from the OS-level
 * global hotkey listener (CGEventTap on macOS, WH_KEYBOARD_LL on Windows)
 * because the widget panel is non-activating and never receives DOM
 * keydown events when the user is focused in another app. The key is
 * consumed at the OS level — it does not reach the foreground app.
 */
export async function listenWidgetEscapePressed(cb: EventCallback<void>) {
  if (!isTauri()) return () => {};
  return listen<void>('widget:escape-pressed', cb);
}
